import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { useSites, type Site } from '@/features/sites/api';
import { cn } from '@/lib/cn';
import { useCasinoAccounts, type CasinoAccount } from '../casino-accounts-api';
import { CasinoAccountForm } from './CasinoAccountForm';
import { ProfileCard } from './ProfileCard';

/**
 * The trusted sites this member plays on, and the identifiers that link them to
 * a leaderboard row.
 *
 * Connected sites first, as compact rows. The ones they have not claimed sit
 * behind "Başka site ekle" rather than appearing as six empty forms — the
 * question this screen asks is "who are you on Stake?", and a wall of blank
 * fields asks it six times at once.
 */
export function TrustedSitesCard() {
  const sites = useSites();
  const accounts = useCasinoAccounts();
  const [editing, setEditing] = useState<string | null>(null);
  const [picking, setPicking] = useState(false);

  const bySlug = new Map((accounts.data ?? []).map((account) => [account.siteSlug, account]));
  const all = sites.data ?? [];

  const connected = all.filter((site) => bySlug.has(site.slug));
  const available = all.filter((site) => !bySlug.has(site.slug));
  const adding = available.find((site) => site.slug === editing);

  function close() {
    setEditing(null);
    setPicking(false);
  }

  return (
    <ProfileCard
      id="trusted-sites"
      title="Güvenilir siteler — ID'lerini bağla"
      description="Oynadığın sitelerdeki hesabını bağla; wager yarışmalarında sıralamada seni tanıyalım."
    >
      {sites.isLoading || accounts.isLoading ? (
        <p className="py-8 text-center text-sm text-surface-500" role="status">
          Yükleniyor…
        </p>
      ) : all.length === 0 ? (
        <p className="py-8 text-center text-sm text-surface-500">Henüz güvenilir site eklenmedi.</p>
      ) : (
        <div className="flex flex-col gap-3">
          {connected.map((site) => (
            <SiteRow
              key={site.slug}
              site={site}
              account={bySlug.get(site.slug)}
              isEditing={editing === site.slug}
              onToggle={() => {
                setPicking(false);
                setEditing(editing === site.slug ? null : site.slug);
              }}
              onSaved={close}
            />
          ))}

          {adding ? (
            <SiteRow site={adding} account={undefined} isEditing onToggle={close} onSaved={close} />
          ) : null}

          {available.length > 0 ? (
            <div className="rounded-xl border border-dashed border-surface-700/80 p-4">
              {picking ? (
                <div>
                  <p className="mb-3 text-xs font-medium uppercase tracking-widest text-surface-500">
                    Hangi site?
                  </p>

                  <ul className="flex flex-wrap gap-2">
                    {available.map((site) => (
                      <li key={site.slug}>
                        <button
                          type="button"
                          onClick={() => {
                            setEditing(site.slug);
                            setPicking(false);
                          }}
                          className="flex min-h-11 items-center gap-2 rounded-full border border-surface-700 px-3 py-1.5 text-sm text-surface-200 transition hover:border-brand-500/60 hover:bg-surface-800"
                        >
                          <SiteIcon site={site} size={20} />
                          {site.name}
                        </button>
                      </li>
                    ))}
                  </ul>

                  <button
                    type="button"
                    onClick={() => setPicking(false)}
                    className="mt-3 text-xs text-surface-500 transition hover:text-surface-300"
                  >
                    Vazgeç
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    setEditing(null);
                    setPicking(true);
                  }}
                  className="flex w-full flex-col items-center gap-1 py-2 text-surface-300 transition hover:text-surface-100"
                >
                  <span className="flex items-center gap-2 font-medium">
                    <svg
                      aria-hidden
                      viewBox="0 0 24 24"
                      className="size-5 fill-none stroke-current"
                      strokeWidth={2}
                    >
                      <path d="M12 5v14M5 12h14" strokeLinecap="round" />
                    </svg>
                    Başka site ekle
                  </span>
                  <span className="text-xs text-surface-500">
                    Ne kadar çok site bağlarsan o kadar çok yarışmada görünürsün.
                  </span>
                </button>
              )}
            </div>
          ) : null}
        </div>
      )}
    </ProfileCard>
  );
}

interface SiteRowProps {
  site: Site;
  account: CasinoAccount | undefined;
  isEditing: boolean;
  onToggle: () => void;
  onSaved: () => void;
}

function SiteRow({ site, account, isEditing, onToggle, onSaved }: SiteRowProps) {
  // The username leads when there is one: it is what the member recognises. The
  // player id is the fallback for partners that export nothing else.
  const identifier = account?.username ?? account?.externalUserId;

  return (
    <div
      className={cn(
        'rounded-xl border transition',
        account
          ? 'border-surface-800 bg-surface-950/50'
          : 'border-brand-500/30 bg-brand-500/[0.04]',
      )}
    >
      <div className="flex flex-wrap items-center gap-3 p-3 sm:gap-4 sm:p-4">
        <SiteIcon site={site} size={40} />

        <div className="min-w-0 flex-1">
          {account ? (
            <>
              <p className="text-xs font-medium uppercase tracking-widest text-surface-500">
                {account.username ? 'Kullanıcı adı' : 'Kullanıcı ID'}
              </p>
              <p className="truncate font-mono text-sm text-surface-100">{identifier}</p>
              <p className="mt-1 flex items-center gap-1.5 text-xs text-emerald-400">
                <span aria-hidden className="size-1.5 rounded-full bg-emerald-400" />
                Bağlı
              </p>
            </>
          ) : (
            <>
              <p className="font-medium text-surface-100">{site.name}</p>
              <p className="text-xs text-surface-500">Kullanıcı adını veya ID&apos;ni gir.</p>
            </>
          )}
        </div>

        <Button
          type="button"
          variant="ghost"
          className="border border-surface-700 px-4 py-2 text-sm"
          aria-expanded={isEditing}
          onClick={onToggle}
        >
          {isEditing ? 'Kapat' : account ? "ID'yi güncelle" : 'Bağla'}
        </Button>
      </div>

      {isEditing ? (
        <div className="border-t border-surface-800 p-3 sm:p-4">
          <CasinoAccountForm site={site} saved={account} onSaved={onSaved} />
        </div>
      ) : null}
    </div>
  );
}

function SiteIcon({ site, size }: { site: Site; size: number }) {
  // The accent comes from the database, so it is an inline style — Tailwind
  // cannot generate a class for a value it has never seen.
  return (
    <span
      className="grid shrink-0 place-items-center rounded-full p-[2px]"
      style={{
        width: size + 4,
        height: size + 4,
        background: `linear-gradient(160deg, ${site.brandColor}66, transparent 70%)`,
      }}
    >
      {site.iconUrl ? (
        <img
          src={site.iconUrl}
          alt=""
          width={size}
          height={size}
          loading="lazy"
          className="rounded-full object-cover"
          style={{ width: size, height: size }}
        />
      ) : (
        <span
          className="grid place-items-center rounded-full bg-surface-800 text-xs font-bold text-surface-400"
          style={{ width: size, height: size }}
        >
          {site.name.slice(0, 1)}
        </span>
      )}
    </span>
  );
}
